import React, { useEffect, useState } from 'react';
import { notificationsAPI } from '../services/api';
import { format } from 'date-fns';
import { Bell, Mail, MessageCircle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const CHANNEL_ICON = { email: Mail, whatsapp: MessageCircle, sms: MessageCircle };
const STATUS_COLOR = { sent: 'text-green-600 bg-green-50', failed: 'text-red-600 bg-red-50', pending: 'text-yellow-600 bg-yellow-50' };

export default function NotificationsPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await notificationsAPI.list(params);
      setLogs(data.results || data);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  const runScheduler = async () => {
    setRunning(true);
    try {
      const { data } = await notificationsAPI.runScheduler();
      toast.success(`Scheduler done: ${data.results?.sent ?? 0} sent, ${data.results?.failed ?? 0} failed`);
      load();
    } catch { toast.error('Failed to run scheduler'); }
    setRunning(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        </div>
        {['admin'].includes(user?.role) && (
          <button onClick={runScheduler} disabled={running}
            className="flex items-center gap-2 bg-blue-950 hover:bg-blue-800 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-60">
            <RefreshCw size={15} className={running ? 'animate-spin' : ''} />
            {running ? 'Running...' : 'Run Reminder Scheduler'}
          </button>
        )}
      </div>

      {/* Filter */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex gap-3 flex-wrap">
        {['', 'sent', 'failed', 'pending'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${filter === s ? 'bg-blue-950 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {s === '' ? 'All' : s}
          </button>
        ))}
      </div>

      {/* Logs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <p className="text-center py-16 text-gray-400">Loading...</p>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Bell size={36} className="mx-auto mb-3 opacity-30" />
            <p>No notification logs found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Case', 'Recipient', 'Channel', 'Trigger', 'Status', 'Sent At'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {logs.map(log => {
                const Icon = CHANNEL_ICON[log.channel] || Bell;
                return (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-blue-900 font-semibold">{log.case_detail?.case_number || log.case}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-800">{log.recipient_name}</p>
                      <p className="text-xs text-gray-400 truncate max-w-[160px]">{log.recipient_contact}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Icon size={14} className="text-gray-500" />
                        <span className="capitalize text-sm text-gray-600">{log.channel}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 capitalize">{log.trigger.replace('_', ' ')}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${STATUS_COLOR[log.status]}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {format(new Date(log.sent_at), 'dd MMM yyyy HH:mm')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
